import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/** An A4 page at 100%, as pdf.js lays it out (595pt at 96dpi), in rem. */
const A4_WIDTH_REM = 49.6;

/** Lines of a page of text (null: a paragraph break): widths vary just enough to read as paragraphs. */
const LINES = [
  { id: "header-1", width: "w-1/3" },
  { id: "header-2", width: "w-1/4" },
  { id: "header-3", width: "w-2/5" },
  { id: "break-1", width: null },
  { id: "body-1", width: "w-full" },
  { id: "body-2", width: "w-full" },
  { id: "body-3", width: "w-11/12" },
  { id: "body-4", width: "w-full" },
  { id: "body-5", width: "w-3/4" },
  { id: "break-2", width: null },
  { id: "body-6", width: "w-full" },
  { id: "body-7", width: "w-5/6" },
];

/** A page of text being loaded: white, the viewer's shadow, lines in place of the text. */
export function PageSkeleton({ zoom = 1 }: { zoom?: number }) {
  return (
    <div
      className="mx-auto flex aspect-[1/1.414] max-w-full flex-col gap-3 bg-white px-[8%] py-[10%] shadow-sm ring-1 ring-black/5"
      style={{ width: `${A4_WIDTH_REM * zoom}rem` }}
    >
      {LINES.map(({ id, width }) =>
        width ? <Skeleton key={id} className={cn("h-3", width)} /> : <div key={id} className="h-3" />,
      )}
    </div>
  );
}

/**
 * Our PDF viewer, before the document is there: the same toolbar and page as `PdfPreview`, so that while the
 * dialog opens, the code arrives and the first bytes download, nothing jumps — the real page takes the
 * skeleton's place. Has no dependency on pdf.js: it shows while that loads.
 */
export function PdfViewerSkeleton({
  className,
  zoom = 1,
  isPagesPanelOpen = false,
}: {
  className?: string;
  zoom?: number;
  isPagesPanelOpen?: boolean;
}) {
  return (
    <output
      aria-busy="true"
      aria-label="Chargement du document"
      className={cn("flex h-[75vh] flex-col overflow-hidden rounded-md border bg-background", className)}
    >
      <div className="flex items-center gap-2 border-b px-2 py-1.5">
        <Skeleton className="size-8 rounded-md" />
        <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
          Page
          <Skeleton className="h-8 w-12 rounded-md" />
        </span>
        <div className="ms-auto flex items-center gap-1">
          <Skeleton className="size-8 rounded-md" />
          <Skeleton className="size-8 rounded-md" />
          <Skeleton className="mx-1 h-5 w-px" />
          <Skeleton className="size-8 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
          <Skeleton className="size-8 rounded-md" />
          <Skeleton className="mx-1 h-5 w-px" />
          <Skeleton className="size-8 rounded-md" />
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        {isPagesPanelOpen && (
          <div className="flex w-44 shrink-0 flex-col items-center gap-3 border-e bg-background p-3">
            <Skeleton className="aspect-[1/1.414] w-28 rounded-sm" />
            <Skeleton className="aspect-[1/1.414] w-28 rounded-sm" />
          </div>
        )}
        <div className="min-w-0 flex-1 overflow-hidden bg-muted pt-6">
          <PageSkeleton zoom={zoom} />
        </div>
      </div>
    </output>
  );
}
